import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import LOGO from "src/assets/svgs/logo.png";
import Box from "@material-ui/core/Box";
import {
  Fade,
  FormGroup,
  IconButton,
  Slide,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Radio from "@material-ui/core/Radio";
import FormControl from "@material-ui/core/FormControl";
import RadioGroup from "@material-ui/core/RadioGroup";
import { Formik } from "formik";
import { UserSetupQuestions } from "src/interfaces/onboarding.interface";
import { updateUserSetupQuestions } from "src/apiService/modules/users";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { Theme } from "src/theme";
import {
  addGeneralLedgerAccount,
  generalLedgerAccountExists,
} from "src/apiService/modules/generalLedgerAccounts";
import type { BankAccount } from "src/interfaces/bank-account.interface";
import { addBankAccount } from "src/apiService/modules/bank-accounts";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { isImplementedIntegration } from "src/pages/GetStarted/Onboarding/utils";
import { USER_PLAN as UserPlan } from "src/enums/common.enum";
import { userPlanSelector } from "src/store/system/selector";

const BorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: theme.shape.borderRadius,
  },
  bar: {
    borderRadius: theme.shape.borderRadius,
  },
}))(LinearProgress);

const useStyles = makeStyles((theme: Theme) => ({
  submitButton: {
    width: "250px",
  },
  checkCircleIcon: {
    color: theme.palette.success.main,
    fontSize: "100px",
  },
  linearProgressBar: {
    width: "40vw",
  },
  logo: {
    height: "10rem",
    filter:
      theme.palette.custom.mode === "dark" ? "brightness(0) invert(1)" : "none",
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.875rem",
    marginTop: "0.5rem",
  },
  heading: {
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.custom.derived.titleOnBg,
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
  },
}));

const SELLING_PLATFORMS = [
  { value: "Ebay", label: "eBay" },
  { value: "Poshmark", label: "Poshmark" },
  { value: "Mercari", label: "Mercari" },
  { value: "Whatnot", label: "Whatnot" },
  { value: "Etsy", label: "Etsy" },
  { value: "Vinted", label: "Vinted" },
  { value: "Depop", label: "Depop" },
  { value: "Amazon", label: "Amazon" },
  { value: "Shopify", label: "Shopify" },
  { value: "WooCommerce", label: "WooCommerce" },
  { value: "StockX", label: "StockX" },
  { value: "District", label: "District" },
  { value: "Encore", label: "Encore" },
  { value: "Grailed", label: "Grailed" },
  { value: "Facebook Marketplace", label: "Facebook Marketplace" },
  { value: "Walmart", label: "Walmart" },
];

export const SetupQuestions = ({ onClose }) => {
  const recordEvent = useRecordEvent();

  // Basic plans can't connect banks/credit cards — the Integrations page hides
  // the connect button for them (and treats a missing plan as basic), so don't
  // ask a question whose "yes" they could never act on.
  const plan = useSelector(userPlanSelector) || UserPlan.BASIC;
  const isBasic = plan === UserPlan.BASIC;

  const [values, setValues] = React.useState<UserSetupQuestions>({
    sellingPlatforms: [],
    connectBank: undefined,
    finishedCards: {
      sellingPlatforms: [],
      connectBank: false,
    },
    inventoryTypeSet: false,
    hasExistingRecords: false,
  });

  const [otherPlatforms, setOtherPlatforms] = React.useState<string[]>([]);
  const [otherInput, setOtherInput] = React.useState("");
  const [otherChecked, setOtherChecked] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [progress, setProgress] = React.useState(0);

  const [isDoneSubmitting, setIsDoneSubmitting] = React.useState(false);
  const [doneQuestions, setDoneQuestions] = React.useState(false);

  const classes = useStyles();

  useEffect(() => {
    if (isSubmitting) {
      const interval = setInterval(() => {
        setProgress((oldProgress) => {
          const diff = Math.random() * 20;
          return Math.min(oldProgress + diff, 100);
        });
      }, 400);
      return () => clearInterval(interval);
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (progress > 99) {
      setIsSubmitting(false);
      const timeouts = [
        setTimeout(() => {
          setIsDoneSubmitting(true);
          timeouts.push(
            setTimeout(async () => {
              await updateUserSetupQuestions(values);
              recordEvent({
                type: "feature-adoption",
                feature: "setup-started",
                platformCount: values.sellingPlatforms.length,
                platforms: values.sellingPlatforms,
              });
            }, 2000)
          );
        }, 1000),
      ];
      return () => {
        for (const timeout of timeouts) {
          clearTimeout(timeout);
        }
      };
    }
  }, [progress, values]);

  const handleOtherField = (value: string) => {
    const splitPlatform = value.split(",");
    setOtherInput(splitPlatform[splitPlatform.length - 1]);
    const finishedPlatforms = splitPlatform.slice(0, splitPlatform.length - 1);
    otherPlatforms.push(...finishedPlatforms);
  };

  const removeOtherPlatform = (platform: string) => {
    const workingArray = [...otherPlatforms];
    const index = workingArray.indexOf(platform);
    if (index > -1) {
      workingArray.splice(index, 1);
    }
    setOtherPlatforms(workingArray);
  };

  const createAccounts = async (platforms: string[]) => {
    for (let i = 0; i < platforms.length; i++) {
      // Integrated platforms pull their own data, so we skip creating a manual
      // sale-platform bank account for them.
      if (
        //platforms[i] === "Amazon" || TODO: Add back in when implemented
        platforms[i] === "Etsy" ||
        platforms[i] === "Ebay" ||
        platforms[i] === "Poshmark" ||
        platforms[i] === "Mercari" ||
        platforms[i] === "Whatnot" ||
        platforms[i] === "Vinted" ||
        platforms[i] === "Depop"
      ) {
      } else {
        const newPlatform = platforms[i];
        // Adding a duplicate platform (e.g. it was already set up on a prior
        // visit) means a general ledger account with this name already exists.
        // Skip the account/bank-account creation silently instead of failing
        // the whole onboarding step with an error.
        if (await generalLedgerAccountExists(newPlatform)) {
          continue;
        }
        const glAccount = await addGeneralLedgerAccount(newPlatform, [
          "assets",
          "bank-account-cash",
        ]);

        const bankAccount: Omit<BankAccount, "id" | "user"> = {
          general_ledger_account_id: glAccount.id,
          name: newPlatform,
          type: "sale-platform",
        };

        await addBankAccount(bankAccount);
      }
    }
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        py="1rem"
        position="relative"
      >
        <img src={LOGO} alt="My Reseller Genie" className={classes.logo} />
        <Tooltip title="Save and finish later">
          <IconButton onClick={onClose} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {!doneQuestions && (
        <>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mb="0.75rem"
          >
            <Typography className={classes.heading} gutterBottom>
              Let's personalize your set-up:
            </Typography>
          </Box>
          <Formik
            initialValues={values}
            validate={(values) => {
              const errors: any = {};

              const totalPlatforms =
                values.sellingPlatforms.length +
                (otherChecked ? otherPlatforms.length : 0);
              if (totalPlatforms === 0) {
                errors.sellingPlatforms =
                  "Please select at least one platform.";
              }

              if (
                !isBasic &&
                (values.connectBank === undefined ||
                  values.connectBank === null)
              ) {
                errors.connectBank = "Please select either Yes or No.";
              }

              return errors;
            }}
            onSubmit={async (values: UserSetupQuestions) => {
              if (otherChecked) values.sellingPlatforms.push(...otherPlatforms);
              // Store an explicit `false` on basic plans: SetupProgress only
              // treats setup as finishable once connectBank is no longer true.
              values.connectBank =
                !isBasic && String(values.connectBank) === "true";
              if (values.hasExistingRecords !== undefined) {
                values.hasExistingRecords =
                  String(values.hasExistingRecords) === "true";
              }
              setValues(values);
              setDoneQuestions(true);
              setIsSubmitting(true);
              createAccounts(values.sellingPlatforms);
            }}
          >
            {({
              handleChange,
              handleSubmit,
              isSubmitting,
              errors,
              submitCount,
              values: formValues,
            }) => (
              <form
                onSubmit={handleSubmit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mb="0.75rem"
                  flexDirection="column"
                >
                  <Typography variant="h6" gutterBottom>
                    1. What platforms are you selling on?
                  </Typography>
                  <Box mb="0.5rem">
                    <Box
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, auto)",
                        gap: "0 1rem",
                        justifyContent: "center",
                      }}
                      mb=".5rem"
                    >
                      {SELLING_PLATFORMS.map((p) => (
                        <FormControlLabel
                          key={p.value}
                          control={
                            <Checkbox
                              name="sellingPlatforms"
                              value={p.value}
                              onChange={handleChange}
                            />
                          }
                          label={p.label}
                        />
                      ))}
                    </Box>
                    <Box display="flex" justifyContent="left" alignItems="left">
                      {otherPlatforms.map((platform) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              key={platform.trim()}
                              name="sellingPlatforms"
                              value={platform.trim()}
                              onChange={() => removeOtherPlatform(platform)}
                              checked={true}
                            />
                          }
                          label={platform.trim()}
                        />
                      ))}
                    </Box>
                    <Box>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="sellingPlatforms"
                              value="other"
                              onChange={(_, checked) => {
                                setOtherChecked(checked);
                              }}
                            />
                          }
                          label="Other"
                        />
                        <TextField
                          variant="standard"
                          placeholder="Enter platform name"
                          onChange={(e) => handleOtherField(e.target.value)}
                          disabled={!otherChecked}
                          value={otherInput}
                          onKeyDown={(e) => {
                            if (e.key === "Tab" || e.key === "Enter") {
                              e.preventDefault();
                              otherPlatforms.push(otherInput.trim());
                              setOtherInput("");
                            }
                          }}
                        />
                      </FormGroup>
                      {errors.sellingPlatforms && (
                        <Typography className={classes.errorText}>
                          {errors.sellingPlatforms}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {formValues.sellingPlatforms.some((p) =>
                  isImplementedIntegration(p)
                ) && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mb="0.75rem"
                    flexDirection="column"
                  >
                    <Typography variant="h6" gutterBottom>
                      2. Would you like to import past data from sale platforms
                      when possible?
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <FormControl>
                        <RadioGroup
                          name="existing-records-group"
                          defaultValue="false"
                        >
                          <FormControlLabel
                            control={
                              <Radio
                                name="hasExistingRecords"
                                onChange={handleChange}
                                value="false"
                              />
                            }
                            label="I'd like to import the data"
                          />
                          <FormControlLabel
                            control={
                              <Radio
                                name="hasExistingRecords"
                                onChange={handleChange}
                                value="true"
                              />
                            }
                            label="I'll enter myself"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Box>
                )}

                {!isBasic && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mb="0.75rem"
                    flexDirection="column"
                  >
                    <Typography variant="h6" gutterBottom>
                      {formValues.sellingPlatforms.some((p) =>
                        isImplementedIntegration(p)
                      )
                        ? "3"
                        : "2"}
                      . Would you like to connect any bank accounts/credit
                      cards?
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <FormControl>
                        <RadioGroup name="radio-buttons-group">
                          <FormControlLabel
                            control={
                              <Radio
                                name="connectBank"
                                onChange={handleChange}
                                value="true"
                              />
                            }
                            label="Yes"
                          />
                          <FormControlLabel
                            control={
                              <Radio
                                name="connectBank"
                                onChange={handleChange}
                                value="false"
                              />
                            }
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Box mb="0.5rem">
                      {submitCount > 0 && errors.connectBank && (
                        <Typography className={classes.errorText}>
                          {errors.connectBank}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box display="flex" justifyContent="center">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className={classes.submitButton}
                    variant="contained"
                    color="primary"
                  >
                    Submit
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </>
      )}
      <Fade in={isSubmitting} timeout={1000} unmountOnExit={true}>
        <Box>
          <Fade
            in={isSubmitting}
            timeout={4000}
            unmountOnExit={true}
            style={{ transitionDelay: `1000ms` }}
          >
            <Box display="flex" justifyContent="center" mb="1.5rem">
              <Typography variant="h4" gutterBottom>
                Creating your personalized set up now...
              </Typography>
            </Box>
          </Fade>
          <Box display="flex" justifyContent="center" mb="1.5rem">
            <BorderLinearProgress
              variant="determinate"
              value={progress}
              className={classes.linearProgressBar}
            />
          </Box>
        </Box>
      </Fade>

      <Slide direction="up" in={isDoneSubmitting}>
        <Box display="flex" justifyContent="center" mb="1.5rem">
          <CheckCircleIcon className={classes.checkCircleIcon} />
        </Box>
      </Slide>
      <Fade
        in={isDoneSubmitting}
        timeout={{ appear: 500, enter: 1000 }}
        unmountOnExit={true}
      >
        <Box>
          <Box
            display="flex"
            justifyContent="center"
            mb="1.5rem"
            style={{ marginBottom: "1.5rem" }}
          >
            <Typography variant="h4" gutterBottom>
              Success! Let’s get crackin’ 🚀
            </Typography>
          </Box>
        </Box>
      </Fade>
    </>
  );
};
